import { Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Stack } from "@mui/material";
import { useLocalStorage } from "@src/appV2/lib/utils";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { useSetSearchParams } from "@src/appV2/lib/utils/useSetSearchParams";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useCredentialRequirementConfiguration } from "@src/appV2/redesign/Documents/useCredentialRequirementConfiguration";
import { useEffect } from "react";

import { type PlacementWithDetails } from "../../api/useFetchPaginatedPlacements";
import { applicationActiveSectionStorageKey, showApplicationsStorageKey } from "../../utils";
import { placementWithDetailsToData } from "../../utils/placement-data";
import { AppliedPlacementCard } from "../AppliedPlacementCard/AppliedPlacementCard";
import { PlacementsAccordionSectionV3 } from "../PlacementAccordionSection/PlacementsAccordionSectionV3";

interface SentApplicationsAccordionSectionProps {
  placements: Array<{
    placementWithDetails: PlacementWithDetails;
    pageIndex: number;
    placementIndex: number;
  }>;
  isLoadingPlacements: boolean;
  hasNextPagePlacements: boolean;
  isFetchingNextPlacementsPage: boolean;
  showApplications: boolean;
  placementCandidateId: string;
  workerTimeZone: string;
  shouldShowAsApplication?: boolean;
  onLoadMore: () => void;
}

export function SentApplicationsAccordionSection(props: SentApplicationsAccordionSectionProps) {
  const {
    placements,
    isLoadingPlacements,
    hasNextPagePlacements,
    isFetchingNextPlacementsPage,
    showApplications,
    placementCandidateId,
    workerTimeZone,
    shouldShowAsApplication,
    onLoadMore,
  } = props;

  const [, setShowApplications] = useLocalStorage<boolean>(
    showApplicationsStorageKey,
    showApplications
  );

  const queryParameters = useSearchParams<{ applications?: string }>();
  const setSearchParams = useSetSearchParams();

  const [expandedApplications, setExpandedApplications] = useLocalStorage(
    applicationActiveSectionStorageKey("Sent Applications"),
    false
  );

  useEffect(() => {
    if (queryParameters.applications === "true") {
      setExpandedApplications(true);
      setSearchParams({ applications: undefined }, { shouldReplace: true });
    }
  }, [queryParameters, setSearchParams, setExpandedApplications]);

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  return (
    <PlacementsAccordionSectionV3
      initialExpanded={expandedApplications}
      title="Sent Applications"
      isLoading={isLoadingPlacements}
      hasNextPage={hasNextPagePlacements ?? false}
      isFetchingNextPage={isFetchingNextPlacementsPage}
      emptyText={
        <Stack justifyContent="center" flex={1} alignItems="center" sx={{ pt: 5 }} spacing={2}>
          <Text variant="body1" sx={{ textAlign: "center" }}>
            You have not applied to any jobs yet!{" "}
          </Text>
          <Text>
            Please visit the{" "}
            <TextButton
              onClick={() => {
                setShowApplications(false);
              }}
            >
              Open Staff Jobs tab
            </TextButton>{" "}
            to apply to jobs.
          </Text>
        </Stack>
      }
      totalPlacementsCount={placements.length}
      placementIds={placements.map((placement) => placement.placementWithDetails.id)}
      renderPlacementCard={(placementId) => {
        const data = placements.find((payload) => payload.placementWithDetails.id === placementId);
        if (!isDefined(data)) {
          return null;
        }

        return (
          <AppliedPlacementCard
            placement={placementWithDetailsToData(
              data.placementWithDetails,
              credentialRequirementConfiguration
            )}
            placementCandidate={{ id: placementCandidateId }}
            pageIndex={data.pageIndex}
            placementIndex={data.placementIndex}
            source="sent_applications"
            worker={{ tmz: workerTimeZone }}
            shouldShowAsApplication={shouldShowAsApplication}
          />
        );
      }}
      onLoadMore={onLoadMore}
    />
  );
}
